import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";

import Pagination from "../elements/Pagination";
import DateRangePicker from "../elements/DateRangePicker";

import searchIcon from "../../resources/icons/search.svg";
import downArrowIcon from "../../resources/icons/down_pointer.svg";

const AdminContractors: React.FC = () => {
  const navigate = useNavigate();
  const rowsPerPage = 10;

  const [contractors, setContractors] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState(1);

  const [currentPage, setCurrentPage] = useState(1);

  const [tempSearch, setTempSearch] = useState("");
  const [search, setSearch] = useState("");
  const [tempStartDate, setTempStartDate] = useState("");
  const [tempEndDate, setTempEndDate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateRangeResetKey, setDateRangeResetKey] = useState(0);

  const [sortOrder, setSortOrder] = useState<string>("");

  const handleSortByName = () => {
    if (sortOrder === "") {
      setSortOrder("asc");
    } else if (sortOrder === "asc") {
      setSortOrder("desc");
    } else {
      setSortOrder("");
    }
  };

  const applyFilters = () => {
    setSearch(tempSearch);
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setTempSearch("");
    setTempStartDate("");
    setTempEndDate("");
    setSearch("");
    setStartDate("");
    setEndDate("");
    setSortOrder("");
    setCurrentPage(1);
    setDateRangeResetKey((prev) => prev + 1);
  };

  const goToContractorProjects = (id: number, name: string) => {
    navigate(`/admin/contractor/${id}`, { state: { name } });
  };

  useEffect(() => {
    const fetchContractors = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;

      try {
        const queryParams = new URLSearchParams();
        queryParams.append("page", currentPage.toString());
        queryParams.append("limit", rowsPerPage.toString());

        if (search) {
          queryParams.append("name", search);
        }
        if (startDate && endDate) {
          queryParams.append("registerStartDate", startDate);
          queryParams.append("registerEndDate", endDate);
        }
        if (sortOrder) {
          queryParams.append("sort", sortOrder);
        }

        const url = `${
          process.env.REACT_APP_URL
        }/admin/contractors?${queryParams.toString()}`;
        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}`, "X-Client-Type": "web" },
        });

        const data = res.data;

        const formatted = data.contractors.map((user: any) => ({
          id: user.id,
          name: user.name,
          email: user.email,
          registryDate: user.memberSince,
          allProjects: user.totalProjectsCount ?? 0,
          activeProjects: user.activeProjectsCount ?? 0,
          cancelledProjects: user.cancelledProjectsCount ?? 0,
          completedProjects: user.completedProjectsCount ?? 0,
        }));

        setContractors(formatted);
        setTotalPages(data.totalPages || data.pageCount || 1);
      } catch {}
    };

    fetchContractors();
  }, [currentPage, search, startDate, endDate, sortOrder]);

  return (
    <div className="flex flex-row">
      <div className="flex-1 p-6 ml-[17.5rem]">
        <h1 className="text-2xl font-bold pb-4 mb-4 border-b border-[#EAECF0]">
          Contractors
        </h1>

        <div className="bg-white mb-6">
          <div className="flex justify-between gap-4 border-[#D0D5DD] pb-4">
            <div className="flex flex-col w-[20rem]">
              <label className="text-sm font-medium text-[#344054] mb-1">
                User
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2">
                  <img src={searchIcon} alt="Search" className="w-5 h-5" />
                </span>
                <input
                  type="text"
                  placeholder="Search by user name..."
                  value={tempSearch}
                  onChange={(e) => setTempSearch(e.target.value)}
                  className="pl-10 pr-3 py-2 border border-[#D0D5DD] rounded w-full text-sm shadow-sm"
                />
              </div>
            </div>
            <div className="flex-1 flex flex-col">
              <label className="text-sm font-medium text-[#344054] mb-1">
                Date of registry
              </label>
              <DateRangePicker
                key={dateRangeResetKey}
                onChange={(start, end) => {
                  setTempStartDate(start);
                  setTempEndDate(end);
                }}
              />
            </div>
            <div className="flex items-end gap-6">
              <button
                onClick={clearFilters}
                className="px-2 py-2 text-[#344054]"
              >
                Clear Filters
              </button>
              <button
                onClick={applyFilters}
                className="px-2 py-2 bg-buttonBlue text-white rounded text-nowrap"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>

        <div className="relative overflow-x-auto border rounded-xl border-[#EAECF0] mb-4">
          <table className="min-w-full table-fixed">
            <thead className="bg-[#F9FAFB] border-b border-[#EAECF0] text-left text-xs font-medium text-[#475467] text-nowrap">
              <tr>
                <th className="flex flex-row gap-2 items-center px-6 py-3 text-left text-xs font-medium text-[#475467]">
                  User
                  <button onClick={handleSortByName}>
                    <img
                      src={downArrowIcon}
                      alt="Sort"
                      className={`w-4 h-4 transform transition-transform duration-200 ${
                        sortOrder === "asc" ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                </th>
                <th className="px-6 py-3">Date of Registry</th>
                <th className="px-6 py-3">All Projects</th>
                <th className="px-6 py-3">Active</th>
                <th className="px-6 py-3">Cancelled</th>
                <th className="px-6 py-3">Completed</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EAECF0]">
              {contractors.map((user, index) => (
                <tr
                  key={user.id}
                  className={index % 2 === 0 ? "bg-[#F9FAFB]" : "bg-white"}
                >
                  <td className="px-6 py-4">
                    <div
                      className="font-medium text-[#101828] truncate max-w-[12rem]"
                      title={user.name}
                    >
                      {user.name}
                    </div>
                    <div
                      className="text-sm text-[#475467] truncate max-w-[12rem]"
                      title={user.email}
                    >
                      {user.email}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-[#475467]">
                    {dayjs(user.registryDate).format("MMM D, YYYY")}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center text-sm font-medium text-[#363F72] bg-[#F9FAFB] border border-[#D5D9EB] rounded-full px-2 py-[2px] w-fit">
                      {user.allProjects ?? 0}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center text-sm font-medium text-[#B54708] bg-[#FFFAEB] border border-[#FEDF89] rounded-full px-2 py-[2px] w-fit">
                      {user.activeProjects ?? 0}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center text-sm font-medium text-[#B42318] bg-[#FEF3F2] border border-[#FECDCA] rounded-full px-2 py-[2px] w-fit">
                      {user.cancelledProjects ?? 0}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center text-sm font-medium text-[#067647] bg-[#ABEFC6] border border-[#067647] rounded-full px-2 py-[2px] w-fit">
                      {user.completedProjects ?? 0}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      className="text-buttonBlue text-nowrap"
                      onClick={() => goToContractorProjects(user.id, user.name)}
                    >
                      See all projects
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {contractors.length === 0 && (
            <div className="text-center text-red-500 py-8">
              No items found with these criteria.
            </div>
          )}

          {totalPages > 1 && contractors.length > 0 && (
            <div className="border-t mt-4 mb-4">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminContractors;
